import { useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/wizard/Field";
import { t } from "@/i18n";

const isLink = (s) => /spotify|music\.apple\.com|^\s*\d{4,}\s*$/.test(s);

const SpotifyIcon = () => <span className="inline-grid h-5 w-5 place-items-center rounded-full bg-[#1DB954] text-black text-[10px] font-black">S</span>;
const AppleIcon = () => <span className="inline-grid h-5 w-5 place-items-center rounded-full bg-[#FA243C] text-white text-[10px] font-black"></span>;

const Initials = ({ name, round }) => (
  <div className={`h-12 w-12 shrink-0 grid place-items-center bg-muted text-muted-foreground text-sm font-bold ${round ? "rounded-full" : "rounded-md"}`}>{(name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}</div>
);

const Row = ({ r, type, source, onPick }) => (
  <button type="button" onClick={() => onPick(r.uri, source)} data-testid={`search-result-${source}-${r.id}`}
    className="panel w-full p-3 text-left flex gap-3 items-center hover:border-primary/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
    {r.image ? <img src={r.image} alt="" className={`h-12 w-12 object-cover shrink-0 ${type === "artist" ? "rounded-full" : "rounded-md"}`} /> : <Initials name={r.name} round={type === "artist"} />}
    <div className="min-w-0 flex-1">
      <p className="font-medium text-sm truncate">{r.name}</p>
      {type === "artist"
        ? <p className="text-xs text-muted-foreground truncate font-mono">{source === "spotify" ? r.uri : `Apple ID: ${r.id}`}{r.genre ? <span className="font-sans"> · {r.genre}</span> : null}{r.followers != null ? <span className="font-sans"> · {t("{n} seguidores", { n: new Intl.NumberFormat().format(r.followers) })}</span> : null}</p>
        : <p className="text-xs text-muted-foreground truncate">{r.artists?.join(", ")} · {r.release_date?.slice(0, 4)} · {r.album_type === "single" ? "Single" : t("Álbum")} · {r.total_tracks} {t("tracks")}</p>}
    </div>
  </button>
);

const Column = ({ title, icon, items, type, source, onPick }) => (
  <div className="space-y-2" data-testid={`search-${type}-${source}`}>
    <h3 className="font-display font-semibold flex items-center gap-2 text-sm">{icon} {title} <span className="text-xs text-muted-foreground font-normal">({items.length})</span></h3>
    {items.length === 0 && <p className="text-xs text-muted-foreground panel p-3">{t("Sin resultados")}</p>}
    <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">{items.map((r) => <Row key={r.id} r={r} type={type} source={source} onPick={onPick} />)}</div>
  </div>
);

export const ImportSearch = ({ type, onPick, placeholder, hint }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!q.trim()) return;
    if (isLink(q)) { onPick(q.trim(), /spotify/.test(q) ? "spotify" : "apple"); return; }
    setBusy(true);
    try { const { data } = await api.post("/import/search", { q, type }); setResults(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="panel p-5">
        <Field label={type === "artist" ? "Buscar artista" : "Buscar álbum o canción"} hint={hint}>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid={`search-${type}-input`} onKeyDown={(e) => e.key === "Enter" && run()} />
            <Button onClick={run} disabled={busy || !q.trim()} className="gap-2 shrink-0" data-testid={`search-${type}-btn`}><Search className="h-4 w-4" /> {t(busy ? "Buscando…" : "Buscar")}</Button>
          </div>
        </Field>
      </div>
      {results && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid={`search-${type}-results`}>
          <Column title="Spotify" icon={<SpotifyIcon />} items={results.spotify} type={type} source="spotify" onPick={onPick} />
          <Column title="Apple Music" icon={<AppleIcon />} items={results.apple} type={type} source="apple" onPick={onPick} />
        </div>
      )}
    </div>
  );
};
